import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, TextField, Alert, Button } from '@mui/material';
import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const formatDatetimeLocal = (ts) => {
  if (!ts) return '';
  try {
    const date = ts?.toDate ? ts.toDate()
      : typeof ts === 'number' ? new Date(ts)
      : new Date(ts);
    if (isNaN(date.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  } catch { return ''; }
};

const getMinDateTime = () => formatDatetimeLocal(Date.now());

export const PickupTimeForm = ({
  pickupTime: initialPickupTime,
  setPickupTime,
  latestPickUpTime: initialLatestPickUpTime,
  setLatestPickUpTime,
  hasTimeWindow,
  setHasTimeWindow,
}) => {
  const { t } = useTranslation();

  const [pickupTime,       setLocalPickupTime]       = useState(initialPickupTime       ?? null);
  const [latestPickUpTime, setLocalLatestPickUpTime] = useState(initialLatestPickUpTime ?? null);

  useEffect(() => { setLocalPickupTime(initialPickupTime             ?? null); }, [initialPickupTime]);
  useEffect(() => { setLocalLatestPickUpTime(initialLatestPickUpTime ?? null); }, [initialLatestPickUpTime]);

  const handlePickupChange = useCallback((e) => {
    const ms = new Date(e.target.value).getTime();
    setLocalPickupTime(ms);
    setPickupTime?.(ms);
  }, [setPickupTime]);

  const handleLatestPickUpChange = useCallback((e) => {
    const ms = new Date(e.target.value).getTime();
    setLocalLatestPickUpTime(ms);
    setLatestPickUpTime?.(ms);
  }, [setLatestPickUpTime]);

  const toggleTimeWindow = useCallback(() => {
    const next = !hasTimeWindow;
    setHasTimeWindow?.(next);
    if (next && pickupTime && !latestPickUpTime) {
      // default latest pickup = pickup + 1h
      const ms = pickupTime + 60 * 60000;
      setLocalLatestPickUpTime(ms);
      setLatestPickUpTime?.(ms);
    }
  }, [hasTimeWindow, setHasTimeWindow, pickupTime, latestPickUpTime, setLatestPickUpTime]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>

        {/* Earliest pickup — always shown */}
        <Box sx={{ flex: hasTimeWindow ? '1 1 calc(50% - 8px)' : '1 1 100%', minWidth: 200 }}>
          <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
            {hasTimeWindow
              ? (t('delivery.earliestPickupTime') || 'Earliest Pickup')
              : (t('delivery.pickupTime')     || 'Pickup Time')}
          </Typography>
          <TextField
            fullWidth size="small" type="datetime-local"
            value={formatDatetimeLocal(pickupTime)}
            onChange={handlePickupChange}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: getMinDateTime() }}
          />
        </Box>

        {/* Latest pickup — only when hasTimeWindow */}
        {hasTimeWindow && (
          <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }}>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
              {t('delivery.latestPickup') || 'Latest Pickup'}
            </Typography>
            <TextField
              fullWidth size="small" type="datetime-local"
              value={formatDatetimeLocal(latestPickUpTime)}
              onChange={handleLatestPickUpChange}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: pickupTime ? formatDatetimeLocal(pickupTime) : getMinDateTime(),
              }}
            />
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button
          variant={hasTimeWindow ? 'contained' : 'outlined'}
          onClick={toggleTimeWindow}
          size="small"
          sx={{ flexShrink: 0 }}
        >
          {hasTimeWindow
            ? (t('delivery.hideTimeWindow') || 'Hide Time Window')
            : (t('delivery.setTimeWindow')  || 'Set Time Window')}
        </Button>
        {hasTimeWindow && (
          <Alert severity="info" icon={<Info size={16} />} sx={{ py: 0.5, fontSize: '0.875rem', flex: 1, minWidth: 200 }}>
            {t('delivery.timeWindowPriceNote') || 'Time window adds 4€ + VAT'}
          </Alert>
        )}
      </Box>
    </Box>
  );
};